import axios from 'axios';
import apiService from '../api/apiService';
import { updateUserToken, setLanguage } from '../store/correcttrackSlice';
import jwtDecode from 'jwt-decode';
import i18n, { SUPPORTED_LOCALES } from '../i18n';

let store;

export const injectStoreForAuth = _store => {
    store = _store;
};

class AuthService {
    _baseUrl = 'api/Auth/';

    async login (username, password) {
        const result = await apiService.postData(this._baseUrl + '/login', {
            username,
            password,
        });
        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
            try {
                const claim = jwtDecode(result.data.data.token)?.locale;
                if (claim && SUPPORTED_LOCALES.includes(claim)) {
                    store.dispatch(setLanguage(claim));
                    i18n.changeLanguage(claim);
                }
            } catch (_) { /* token without locale claim is fine */ }
        }
        return result;
    }

    /**
     * Re-establishes a session on page load. The access token is held in memory only, so every
     * reload starts without one; the httpOnly refresh cookie survives, so trade it for a fresh
     * access token before the app renders. Deliberately uses bare axios rather than the app's
     * instance: a rejection here just means "not signed in", and routing it through the 401
     * interceptor would send it straight back into this same endpoint.
     * @return {Promise<boolean>} whether a session was restored.
     */
    async restoreSession () {
        try {
            const { data } = await axios.post('/api/Auth/refresh', null, { withCredentials: true });
            if (data?.data?.token) {
                store.dispatch(updateUserToken(data.data));
                return true;
            }
        } catch (_) {
            // No cookie, or the server rejected it. Fall through to the signed-out state.
        }

        return false;
    }

    async logout () {
        // The session to end is identified by the refresh cookie the browser attaches.
        const result = await apiService.postData(this._baseUrl + '/logout', null);
        if (result.status === 200) {
            return true;
        }
        return false;
    }

    /**
     * Start impersonating a user as a super admin. On success the stored session is replaced with
     * the impersonated one, so callers should send the user to a fresh page afterwards rather than
     * leaving components holding data loaded as the previous identity.
     * @param targetUserId {number}
     * @return {Promise<ServiceResponse<JwtResponseDto>>}
     */
    async impersonate (targetUserId) {
        const result = await apiService.postData(this._baseUrl + '/impersonate', {
            targetUserId,
        });

        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
        }

        return result.data;
    }

    /**
     * End an impersonation session and return to the super admin's own account.
     * @return {Promise<ServiceResponse<JwtResponseDto>>}
     */
    async stopImpersonating () {
        // Both halves are read server-side: the impersonated account from the caller's access
        // token, the super admin to restore from the refresh cookie's stored row.
        const result = await apiService.postData(this._baseUrl + '/stop-impersonating', null);

        if (result.status === 200 && result.data.success) {
            store.dispatch(updateUserToken(result.data.data));
        }

        return result.data;
    }

    /**
     * Request a password reset
     * @param username {string}
     * @return {Promise<ServiceResponse<boolean>>}
     */
    async requestResetPassword(username) {
        const result = await apiService.postData(`${this._baseUrl}/reset-password/${username}`, null);

        return result.data;
    }

    /**
     * Submit password reset
     * @param passwordRequest {PasswordResetSubmissionDTO}
     * @return {Promise<ServiceResponse<boolean>>}
     */
    async submitChangedPassword(passwordRequest) {
        const result = await apiService.postData(`${this._baseUrl}/reset-user-password`, passwordRequest);

        return result.data;
    }
}

const authService = new AuthService();
export default authService;
